import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DemoModal() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [state, setState] = useState("idle");

  useEffect(() => {
    const handler = () => {
      setState("idle");
      setOpen(true);
    };
    window.addEventListener("open-demo-modal", handler);
    return () => window.removeEventListener("open-demo-modal", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || state === "sending") return;
    setState("sending");
    try {
      const res = await fetch(`${API}/demo-bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setState("success");
    } catch {
      setState("error");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="demo-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-6 backdrop-blur-md"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="glass-panel relative w-full max-w-md overflow-hidden rounded-2xl border-gold/20 bg-[#0A0A0A]/95 p-8 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.9),0_0_80px_rgba(216,192,138,0.12)]"
          >
            <div className="absolute -top-24 left-1/2 h-48 w-72 -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(216,192,138,0.15),transparent_70%)] blur-2xl" />
            <button
              type="button"
              data-testid="demo-close-button"
              onClick={() => setOpen(false)}
              className="absolute right-5 top-5 text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            {state === "success" ? (
              <div className="relative py-8 text-center" data-testid="demo-success-message">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-gold/40 bg-gold/10 shadow-[0_0_40px_rgba(216,192,138,0.3)]">
                  <Check className="h-7 w-7 text-gold" />
                </div>
                <h3 className="mt-6 font-display text-2xl font-light tracking-tight text-[#F5F5F5]">
                  Request Received.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#A3A3A3]">
                  Our team will reach out within 24 hours to schedule your
                  private walkthrough of the Alkyrvis workforce.
                </p>
                <button
                  type="button"
                  data-testid="demo-done-button"
                  onClick={() => setOpen(false)}
                  className="mt-8 rounded-full border border-gold/40 px-7 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-gold transition-[background-color] duration-300 hover:bg-gold/10"
                >
                  Done
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center gap-3">
                  <img
                    src="/assets/emblem.png"
                    alt=""
                    className="h-10 w-10 object-contain drop-shadow-[0_0_14px_rgba(216,192,138,0.6)]"
                  />
                  <div>
                    <h3 className="font-display text-xl font-light tracking-tight text-[#F5F5F5]">
                      Book A Demo
                    </h3>
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold/80">
                      Private walkthrough — 30 minutes
                    </p>
                  </div>
                </div>

                <div className="mt-7 space-y-4">
                  <input
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name *"
                    data-testid="demo-name-input"
                    className="w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-sm text-[#F5F5F5] outline-none transition-[border-color] duration-300 placeholder:text-white/25 focus:border-gold/50"
                  />
                  <input
                    value={form.email}
                    onChange={set("email")}
                    type="email"
                    placeholder="Work email *"
                    data-testid="demo-email-input"
                    className="w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-sm text-[#F5F5F5] outline-none transition-[border-color] duration-300 placeholder:text-white/25 focus:border-gold/50"
                  />
                  <input
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Company"
                    data-testid="demo-company-input"
                    className="w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-sm text-[#F5F5F5] outline-none transition-[border-color] duration-300 placeholder:text-white/25 focus:border-gold/50"
                  />
                  <textarea
                    value={form.message}
                    onChange={set("message")}
                    placeholder="What would you like to automate first?"
                    rows={3}
                    data-testid="demo-message-input"
                    className="w-full resize-none rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-sm text-[#F5F5F5] outline-none transition-[border-color] duration-300 placeholder:text-white/25 focus:border-gold/50"
                  />
                </div>

                {state === "error" && (
                  <p
                    data-testid="demo-error-message"
                    className="mt-3 font-mono text-[11px] tracking-wide text-red-400"
                  >
                    Something went wrong. Please try again.
                  </p>
                )}

                <button
                  type="button"
                  data-testid="demo-submit-button"
                  onClick={submit}
                  disabled={state === "sending" || !form.name.trim() || !form.email.trim()}
                  className="group mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-gold py-3.5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#050505] transition-[box-shadow,background-color,transform] duration-300 hover:bg-gold-bright hover:shadow-[0_0_40px_rgba(216,192,138,0.45)] active:scale-[0.98] disabled:opacity-40"
                >
                  {state === "sending" ? "Sending..." : "Request My Demo"}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
